import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Checkbox } from '../ui/checkbox-shadcn';
import { Table, TableBody, TableHeader, TableRow, TableCell } from '@/components/ui/table';
import { cn } from '@/lib/utils';

import {
  ProjectOverviewTableHeader,
  ProjectOverviewTableRow,
  ProjectOverviewTableRowProps,
} from './project_overview_table_row';
import {
  PROJECT_OVERVIEW_COLUMNS,
  isColumnVisible,
  type ProjectOverviewColumnKey,
  type ProjectOverviewColumnVisibility,
} from './column_visibility';
import MagnifierIcon from '../../assets/icons/magnifier.svg';
import type {
  RoleAssignmentOptionsByRoleId,
  RoleAssignmentRole,
} from '../../helper/roleAssignments';
import { InvitePanelMode } from '../../pages/team_settings';
import { ScrollToPosition } from "../../containers/application";
import PrintModal from '../../modals/PrintModal';
import type { CustomColumnPreference } from '../../api/column-visibility-preferences';
import {
  CUSTOM_COLUMN_ID_BY_VISIBILITY_KEY,
  isCustomColumnVisibilityKey,
  type CustomColumnId,
} from '../orderingAndReceiving/custom_columns';

export interface ProjectOverviewTableProps {
  rows?: ProjectOverviewTableRowProps['data'][];
  projectList?: ProjectOverviewTableRowProps['data'][];
  enableSorting?: boolean;
  showClient?: boolean;
  className?: string;
  checkedProjects?: number[];
  columnVisibility?: ProjectOverviewColumnVisibility;
  allowDropdownOverflow?: boolean;
  onCheckRow?: (id: number, checked: boolean) => void;
  onCheckAllRows?: (checked: boolean) => void;
  onItemColumnClick?: any;
  onWorkOrderColumnClick?: (id: number) => void;
  onEdited?: (projects: any[]) => void;
  updateNotebook?: (projId: number, filterOut: string) => void;
  onActionSelected?: (value: string, type?: string, selectedProjectIds?: number[]) => void;
  handleNotCheckedBatchRow?: (id: number, project: any) => void;
  onOpenModal?: (value: string) => void;
  handleEstimate?: (projectIds: number[], source: string) => void;
  handleProcessEstimate?: () => void;
  onOpenEstimatingTab?: () => void;
  onOpenEstimate?: (estimateId: number, estimate?: any) => void;
  isConnected?: boolean;
  handlePrint?: () => void;
  isPrintModalOpen?: boolean;
  openPrintModal?: (value: boolean) => void;
  handleOpenSinglePurchasedGoods?: (val: number | null) => void;
  onClientClick?: (id?: number) => void;
  tableStatuses?: any[];
  handleTriggerRefetch?: () => void;
  handleTriggerProjectRefetch?: () => void;
  handleOpenComponents?: (id: number | null) => void;
  handleSaveComponents?: (components: any) => void;
  mapSortTableToSortOptions?: (sortTable: SortTable) => void;
  onCheckRoomRows?: (roomProjectIds: number[], checked: boolean) => void;
  groupRowsByRoom?: boolean;
  canOpenItems?: boolean;
  canManageActions?: boolean;
  roleAssignmentColumns?: RoleAssignmentRole[];
  assignmentOptionsByRoleId?: RoleAssignmentOptionsByRoleId;
  accountIsAdmin?: boolean;
  customColumns?: CustomColumnPreference[];
  onCustomColumnValueChange?: (project: any, columnId: CustomColumnId, value: string) => Promise<boolean> | boolean | void;
  [key: string]: any;
  accountUser?: any;
  canAccessTeamSettings?: boolean;
  handleOpenInvitePanel?(mode: InvitePanelMode, role?: any): void;
  roleAssignmentTeamMembers?: any[];
  roleAssignmentAssignees?: any[];
  handleUpdateTeamMember?(val: any, member: any, role: any): void;
  handleUpdateAssignee?(val: any, assignee: any, role: any): void;
  backToTop?: boolean
  pageScrollTo?(position: ScrollToPosition, val: number): void
}

export interface SortTable {
  field: string;
  position: number;
}

const getEstimateStatusValue = (row: any): string => {
  if (typeof row?.estimateStatus === 'string' && row.estimateStatus) {
    return row.estimateStatus;
  }
  if (typeof row?.estimateStatusSummary?.status === 'string' && row.estimateStatusSummary.status) {
    return row.estimateStatusSummary.status;
  }
  return '';
};

const getEstimateDateValue = (row: any): number | null => {
  const raw =
    row?.estimateDate !== undefined && row?.estimateDate !== ''
      ? row.estimateDate
      : row?.estimateDateSent !== undefined && row?.estimateDateSent !== ''
        ? row.estimateDateSent
        : row?.estimateStatusSummary?.statusDate;
  if (!raw) return null;
  const date = raw instanceof Date ? raw : new Date(raw);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};

const sortEstimateStatusThenDate = (rows: any[], direction: number) => {
  const statusOrder: string[] = [];
  const groups = new Map<string, any[]>();

  rows.forEach((row) => {
    const status = getEstimateStatusValue(row);
    if (!groups.has(status)) {
      groups.set(status, []);
      statusOrder.push(status);
    }
    groups.get(status)!.push(row);
  });

  const compareDates = (a: any, b: any) => {
    const aDate = getEstimateDateValue(a);
    const bDate = getEstimateDateValue(b);
    if (aDate === null && bDate === null) return 0;
    if (aDate === null) return 1;
    if (bDate === null) return -1;
    return direction === 1 ? aDate - bDate : bDate - aDate;
  };

  return statusOrder.flatMap((status) => {
    const list = groups.get(status) || [];
    return [...list].sort(compareDates);
  });
};

const coerceNumericId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const getRowId = (row: any, fallback: number): number => {
  const itemId = coerceNumericId(row?.itemId);
  if (itemId !== null) return itemId;
  const id = coerceNumericId(row?.id);
  if (id !== null) return id;
  return fallback;
};

const VIRTUALIZED_OVERVIEW_ROW_HEIGHT = 112;
const VIRTUALIZED_OVERVIEW_ROW_OVERSCAN = 6;
const VIRTUALIZED_OVERVIEW_ROW_THRESHOLD = 30;
const PROJECT_OVERVIEW_COLUMN_WIDTHS: Record<ProjectOverviewColumnKey, string> = {
  client: 'var(--po-col-client)',
  item: 'var(--po-col-item)',
  status: 'var(--po-col-status)',
  notebook: 'var(--po-col-notes)',
  wopo: 'var(--po-col-wopo)',
  uploads: 'var(--po-col-uploads)',
  estimate: 'var(--po-col-estimate)',
  invoice: 'var(--po-col-invoice)',
  components: 'var(--po-col-components)',
  production: 'var(--po-col-production)',
  install: 'var(--po-col-install)',
  customColumn1: 'var(--po-col-custom)',
  customColumn2: 'var(--po-col-custom)',
  customColumn3: 'var(--po-col-custom)',
};

const ProjectOverviewTable: React.FC<ProjectOverviewTableProps> = ({
  projectList = [],
  enableSorting = true,
  showClient = true,
  className,
  checkedProjects = [],
  columnVisibility,
  allowDropdownOverflow = false,
  onCheckRow,
  onCheckAllRows,
  onItemColumnClick,
  onWorkOrderColumnClick,
  onEdited,
  updateNotebook,
  onActionSelected,
  handleNotCheckedBatchRow,
  onOpenModal,
  onOpenEstimatingTab,
  onOpenEstimate,
  handlePrint,
  isPrintModalOpen = false,
  openPrintModal,
  handleOpenSinglePurchasedGoods,
  onClientClick,
  tableStatuses,
  handleTriggerRefetch,
  handleTriggerProjectRefetch,
  handleSaveComponents,
  mapSortTableToSortOptions,
  onCheckRoomRows,
  groupRowsByRoom = false,
  canOpenItems = true,
  canManageActions = true,
  roleAssignmentColumns = [],
  assignmentOptionsByRoleId = {},
  accountIsAdmin = false,
  customColumns = [],
  onCustomColumnValueChange,
  isFiltersActivate = false,
  handleResetAllFilters,
  accountUser,
  canAccessTeamSettings,
  handleOpenInvitePanel,
  roleAssignmentTeamMembers = [],
  roleAssignmentAssignees = [],
  handleUpdateTeamMember,
  handleUpdateAssignee,
  backToTop,
  pageScrollTo,
}) => {
  const isClientPageTable =
    typeof className === 'string' && className.includes('client-project-overview-table');
  const headerWrapperRef = useRef<HTMLDivElement | null>(null);
  const bodyWrapperRef = useRef<HTMLDivElement | null>(null);
  const syncingHorizontalScroll = useRef(false);
  const scrollFrameRef = useRef<number | null>(null);
  const pendingScrollMetricsRef = useRef({ scrollTop: 0, clientHeight: 0 });
  const [componentsModalProject, setComponentsModalProject] = useState<number>(-1);
  const [bodyScrollbarGutter, setBodyScrollbarGutter] = useState(0);
  const [scrollMetrics, setScrollMetrics] = useState({ scrollTop: 0, clientHeight: 0 });
  const defaultSortField = showClient ? 'clientId' : 'itemTypeId';
  const [sortTable, setSortTable] = useState<SortTable>(() => ({
    field: defaultSortField,
    position: 1,
  }));

  useEffect(() => {
    if (!enableSorting) return;
    mapSortTableToSortOptions?.(sortTable);
  }, [enableSorting, mapSortTableToSortOptions, sortTable]);

  useEffect(() => {
    setSortTable({ field: defaultSortField, position: 1 });
  }, [defaultSortField]);

  const handleSortLabel = (field: string) => {
    setSortTable((prev) => {
      const isDefault = field === defaultSortField;
      if (prev.field !== field) {
        return { field, position: 1 };
      }
      if (isDefault) {
        return { field, position: prev.position === 1 ? 2 : 1 };
      }
      if (prev.position === 1) {
        return { field, position: 2 };
      }
      return { field: defaultSortField, position: 1 };
    });
  };

  const handleSortArrow = (field: string) => {
    setSortTable((prev) => {
      if (prev.field !== field) {
        return { field, position: 1 };
      }
      return { field, position: prev.position === 1 ? 2 : 1 };
    });
  };

  const handleActionMenu = (value: string, type?: string, selectedProjectIds?: number[]) => {
    if (typeof onActionSelected === 'function') {
      onActionSelected(value, type, selectedProjectIds);
    }
  };

  const handleOpenComponents = (id: number | null) => {
    setComponentsModalProject(id || -1);
  };

  const sortedProjectList = React.useMemo(() => {
    if (sortTable.field === 'estimateStatus') {
      return sortEstimateStatusThenDate(projectList, sortTable.position);
    }
    return projectList;
  }, [projectList, sortTable.field, sortTable.position]);

  const customColumnIds = React.useMemo(
    () => new Set(customColumns.map((column) => column.id as CustomColumnId)),
    [customColumns]
  );

  const isProjectColumnVisible = React.useCallback(
    (key: ProjectOverviewColumnKey) => {
      if (key === 'client' && !showClient) return false;
      if (!isColumnVisible(columnVisibility, key)) return false;
      if (isCustomColumnVisibilityKey(key)) {
        return customColumnIds.has(CUSTOM_COLUMN_ID_BY_VISIBILITY_KEY[key]);
      }
      return true;
    },
    [columnVisibility, customColumnIds, showClient]
  );

  const visibleColumnCount = React.useMemo(() => {
    return PROJECT_OVERVIEW_COLUMNS.reduce((count, column) => {
      if (!isProjectColumnVisible(column.key)) return count;
      return count + 1;
    }, 0);
  }, [isProjectColumnVisible]);

  const emptyColSpan = 2 + visibleColumnCount;
  const fullColSpan = emptyColSpan + roleAssignmentColumns.length;
  const visibleColumnWidths = React.useMemo(() => {
    const widths = ['var(--po-col-select)'];

    PROJECT_OVERVIEW_COLUMNS.forEach((column) => {
      if (!isProjectColumnVisible(column.key)) return;
      widths.push(PROJECT_OVERVIEW_COLUMN_WIDTHS[column.key]);
    });

    roleAssignmentColumns.forEach(() => widths.push('var(--po-col-role)'));
    widths.push('55px');
    return widths;
  }, [isProjectColumnVisible, roleAssignmentColumns]);

  const renderColumnGroup = () => (
    <colgroup>
      {visibleColumnWidths.map((width, index) => (
        <col key={`${width}-${index}`} style={{ width }} />
      ))}
    </colgroup>
  );

  const groupedRowsByRoom = React.useMemo(() => {
    if (!groupRowsByRoom) return [];

    const grouped = sortedProjectList.reduce(
      (acc, row, index) => {
        const rawRoom = row?.roomName ?? row?.customRoom ?? row?.room;
        const roomName =
          typeof rawRoom === 'string' && rawRoom.trim() !== '' ? rawRoom : 'Unassigned';
        if (!acc[roomName]) {
          acc[roomName] = [];
        }
        acc[roomName].push({ row, index });
        return acc;
      },
      {} as Record<string, Array<{ row: any; index: number }>>
    );

    return Object.entries(grouped)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([roomName, rows]) => ({ roomName, rows }));
  }, [groupRowsByRoom, sortedProjectList]);

  const selectedProjectList = React.useMemo(
    () =>
      sortedProjectList.filter((row, index) => {
        const rowId = getRowId(row, index);
        return checkedProjects.includes(rowId);
      }),
    [checkedProjects, sortedProjectList]
  );

  const preparedSelectedProjectCount = selectedProjectList.filter((project) =>
    project?.hasOwnProperty?.('workOrderId')
  ).length;

  const renderProjectRow = (row: any, index: number, keyPrefix = '') => {
    const rowId = getRowId(row, index);
    const rowKey = keyPrefix ? `${keyPrefix}-${rowId}-${index}` : rowId;
    return (
      <ProjectOverviewTableRow
        key={rowKey}
        id={rowId}
        showClient={showClient}
        data={row}
        checkedProjects={checkedProjects}
        columnVisibility={columnVisibility}
        onCheckRow={onCheckRow}
        onActionMenu={handleActionMenu}
        handleNotCheckedBatchRow={handleNotCheckedBatchRow}
        onOpenModal={onOpenModal}
        onItemClick={onItemColumnClick}
        canOpenItems={canOpenItems}
        canManageActions={canManageActions}
        onWorkOrderClick={onWorkOrderColumnClick}
        onEdited={onEdited}
        updateNotebook={updateNotebook}
        onOpenEstimatingTab={onOpenEstimatingTab}
        onOpenEstimate={onOpenEstimate}
        handleOpenSinglePurchasedGoods={handleOpenSinglePurchasedGoods}
        onClientClick={onClientClick}
        tableStatuses={tableStatuses}
        handleTriggerRefetch={handleTriggerRefetch}
        handleTriggerProjectRefetch={handleTriggerProjectRefetch}
        handleOpenComponents={handleOpenComponents}
        componentsModalProject={componentsModalProject === rowId}
        handleSaveComponents={handleSaveComponents}
        accountUser={accountUser}
        roleAssignmentColumns={roleAssignmentColumns}
        assignmentOptionsByRoleId={assignmentOptionsByRoleId}
        accountIsAdmin={accountIsAdmin}
        canAccessTeamSettings={canAccessTeamSettings}
        handleOpenInvitePanel={handleOpenInvitePanel}
        roleAssignmentTeamMembers={roleAssignmentTeamMembers}
        roleAssignmentAssignees={roleAssignmentAssignees}
        handleUpdateTeamMember={handleUpdateTeamMember}
        handleUpdateAssignee={handleUpdateAssignee}
        customColumns={customColumns}
        onCustomColumnValueChange={onCustomColumnValueChange}
      />
    );
  };

  const shouldVirtualizeRows =
    !groupRowsByRoom && sortedProjectList.length > VIRTUALIZED_OVERVIEW_ROW_THRESHOLD;
  const viewportHeight = scrollMetrics.clientHeight || bodyWrapperRef.current?.clientHeight || 0;
  const virtualRowCount = shouldVirtualizeRows
    ? Math.ceil(viewportHeight / VIRTUALIZED_OVERVIEW_ROW_HEIGHT) +
      VIRTUALIZED_OVERVIEW_ROW_OVERSCAN * 2
    : sortedProjectList.length;
  const rawVirtualStartIndex = shouldVirtualizeRows
    ? Math.max(
        0,
        Math.floor(scrollMetrics.scrollTop / VIRTUALIZED_OVERVIEW_ROW_HEIGHT) -
          VIRTUALIZED_OVERVIEW_ROW_OVERSCAN
      )
    : 0;
  const virtualStartIndex = shouldVirtualizeRows
    ? Math.min(Math.max(0, sortedProjectList.length - virtualRowCount), rawVirtualStartIndex)
    : 0;
  const virtualEndIndex = shouldVirtualizeRows
    ? Math.min(sortedProjectList.length, virtualStartIndex + virtualRowCount)
    : sortedProjectList.length;
  const virtualProjectRows = shouldVirtualizeRows
    ? sortedProjectList.slice(virtualStartIndex, virtualEndIndex)
    : sortedProjectList;
  const topSpacerHeight = shouldVirtualizeRows
    ? virtualStartIndex * VIRTUALIZED_OVERVIEW_ROW_HEIGHT
    : 0;
  const bottomSpacerHeight = shouldVirtualizeRows
    ? Math.max(0, sortedProjectList.length - virtualEndIndex) *
      VIRTUALIZED_OVERVIEW_ROW_HEIGHT
    : 0;

  const handleRoomSelectionChange = (roomProjectIds: number[], checked: boolean) => {
    if (onCheckRoomRows) {
      onCheckRoomRows(roomProjectIds, checked);
      return;
    }
    roomProjectIds.forEach((projectId) => {
      onCheckRow?.(projectId, checked);
    });
  };

  const syncHorizontalScroll = (source: HTMLDivElement | null, target: HTMLDivElement | null) => {
    if (!source || !target) return;
    if (syncingHorizontalScroll.current) return;
    if (target.scrollLeft === source.scrollLeft) return;
    syncingHorizontalScroll.current = true;
    target.scrollLeft = source.scrollLeft;
    requestAnimationFrame(() => {
      syncingHorizontalScroll.current = false;
    });
  };

  const updateScrollMetrics = useCallback((scrollTop: number, clientHeight: number) => {
    pendingScrollMetricsRef.current = { scrollTop, clientHeight };

    if (scrollFrameRef.current !== null) return;

    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      setScrollMetrics((current) => {
        const next = pendingScrollMetricsRef.current;
        return current.scrollTop === next.scrollTop && current.clientHeight === next.clientHeight
          ? current
          : next;
      });
    });
  }, []);

  const updateBodyScrollbarGutter = useCallback(() => {
    const wrapper = bodyWrapperRef.current;
    if (!wrapper || !shouldVirtualizeRows) {
      setBodyScrollbarGutter((current) => (current === 0 ? current : 0));
      return;
    }

    const style = window.getComputedStyle(wrapper);
    const borderWidth =
      Number.parseFloat(style.borderLeftWidth || '0') +
      Number.parseFloat(style.borderRightWidth || '0');
    const next = Math.max(0, Math.round(wrapper.offsetWidth - wrapper.clientWidth - borderWidth));
    setBodyScrollbarGutter((current) => (current === next ? current : next));
  }, [shouldVirtualizeRows]);

  const handleHeaderWrapperScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    syncHorizontalScroll(event.currentTarget, bodyWrapperRef.current);
  };

  const handleBodyWrapperScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    syncHorizontalScroll(event.currentTarget, headerWrapperRef.current);
    if (shouldVirtualizeRows) {
      updateScrollMetrics(event.currentTarget.scrollTop, event.currentTarget.clientHeight);
    }
  };

  useLayoutEffect(() => {
    const wrapper = bodyWrapperRef.current;
    if (!wrapper || !shouldVirtualizeRows) return;

    setScrollMetrics((current) => {
      const next = {
        scrollTop: wrapper.scrollTop,
        clientHeight: wrapper.clientHeight,
      };

      return current.scrollTop === next.scrollTop && current.clientHeight === next.clientHeight
        ? current
        : next;
    });
    updateBodyScrollbarGutter();
  }, [
    shouldVirtualizeRows,
    sortedProjectList.length,
    showClient,
    columnVisibility,
    roleAssignmentColumns.length,
    customColumns.length,
    updateBodyScrollbarGutter,
  ]);

  useLayoutEffect(() => {
    updateBodyScrollbarGutter();

    const wrapper = bodyWrapperRef.current;
    if (!wrapper || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(updateBodyScrollbarGutter);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [updateBodyScrollbarGutter]);

  useEffect(() => {
    return () => {
      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!headerWrapperRef.current || !bodyWrapperRef.current) return;
    headerWrapperRef.current.scrollLeft = bodyWrapperRef.current.scrollLeft;
  }, [sortedProjectList.length, showClient, columnVisibility, groupRowsByRoom]);

  return (
    <div
      className={cn('flex min-w-0 flex-1 min-h-0 flex-col', className)}
      style={{ '--po-scrollbar-gutter': `${bodyScrollbarGutter}px` } as React.CSSProperties}
    >
      <div className={cn('flex-1 min-w-0 min-h-0 overflow-visible')}>
        <div className="sticky top-0 z-40 bg-white">
          <Table
            className={cn(
              'table-fixed project-overview-table w-full',
              !isClientPageTable && 'po-overview-layout',
              isClientPageTable ? 'min-w-full' : 'min-w-[1600px]'
            )}
            wrapperRef={headerWrapperRef}
            onWrapperScroll={handleHeaderWrapperScroll}
            wrapperClassName={cn(
              'project-overview-header-wrapper overflow-x-auto overflow-y-visible max-h-none border-b-0 rounded-b-none'
            )}
          >
            {renderColumnGroup()}
            <TableHeader>
              <ProjectOverviewTableHeader
                enableSorting={enableSorting}
                showClient={showClient}
                checkedProjects={checkedProjects}
                totalRows={sortedProjectList.length}
                onCheckAllRows={onCheckAllRows}
                columnVisibility={columnVisibility}
                projectList={sortedProjectList}
                onActionSelected={onActionSelected}
                onOpenModal={onOpenModal}
                onOpenEstimatingTab={onOpenEstimatingTab}
                handlePrint={handlePrint}
                openPrintModal={openPrintModal}
                sortTable={sortTable}
                onSortLabel={handleSortLabel}
                onSortArrow={handleSortArrow}
                canOpenItems={canOpenItems}
                canManageActions={canManageActions}
                roleAssignmentColumns={roleAssignmentColumns}
                customColumns={customColumns}
                backToTop={backToTop}
                pageScrollTo={pageScrollTo}
              />
            </TableHeader>
          </Table>
        </div>

        <Table
          className={cn(
            'table-fixed project-overview-table',
            !isClientPageTable && 'po-overview-layout',
            isClientPageTable ? 'w-max min-w-full' : 'w-full min-w-[1600px]'
          )}
          wrapperRef={bodyWrapperRef}
          onWrapperScroll={handleBodyWrapperScroll}
          wrapperClassName={cn(
            '!pb-[40px] overflow-x-auto overscroll-x-contain border-t-0 rounded-t-none quipa-hide-bottom-horizontal-scrollbar',
            shouldVirtualizeRows
              ? '!max-h-[calc(100vh-300px)] !overflow-y-auto overscroll-y-auto'
              : 'md:h-full h-full md:max-h-full max-h-full overflow-y-visible overscroll-y-auto max-h-none'
          )}
        >
          {renderColumnGroup()}
          <TableBody
            className="[&_tr:last-child]:border"
            data-row-count={sortedProjectList.length}
            data-virtualized={shouldVirtualizeRows ? 'true' : 'false'}
          >
            {sortedProjectList.length ? (
              groupRowsByRoom ? (
                groupedRowsByRoom.map(({ roomName, rows }: { roomName: string; rows: any }) => {
                  const roomProjectIds = rows
                    .map(({ row, index }) => getRowId(row, index))
                    .filter((id): id is number => Number.isFinite(id));
                  const selectedCount = roomProjectIds.filter((id) =>
                    checkedProjects.includes(id)
                  ).length;
                  const isAllInRoomSelected =
                    roomProjectIds.length > 0 && selectedCount === roomProjectIds.length;
                  const isPartiallySelected =
                    selectedCount > 0 && selectedCount < roomProjectIds.length;

                  return (
                    <React.Fragment key={roomName}>
                      <TableRow className="border-b transition-colors data-[state=selected]:bg-muted bg-[#ebf4f7] hover:bg-[#e1eff4]">
                        <TableCell
                          colSpan={fullColSpan}
                          className="px-0 py-[10px] text-left text-[16px] font-medium text-[#475569] last:!static last:!w-auto last:!text-left group-data-[scrollable=true]:last:!shadow-none"
                          style={{ textAlign: 'left' }}
                        >
                          <div className="flex w-full items-center gap-3">
                            <div className="po-room-select-cell flex w-[44px] min-w-[44px] max-w-[44px] items-center justify-center">
                              <Checkbox
                                aria-label={`Select room ${roomName}`}
                                checked={
                                  isPartiallySelected ? 'indeterminate' : isAllInRoomSelected
                                }
                                onCheckedChange={(checked) =>
                                  handleRoomSelectionChange(roomProjectIds, checked === true)
                                }
                              />
                            </div>
                            <div className="w-full text-left">{roomName}</div>
                          </div>
                        </TableCell>
                      </TableRow>
                      {rows.map(({ row, index }) => renderProjectRow(row, index, roomName))}
                    </React.Fragment>
                  );
                })
              ) : (
                <>
                  {topSpacerHeight > 0 && (
                    <TableRow aria-hidden="true" className="border-0 hover:bg-transparent">
                      <TableCell
                        colSpan={fullColSpan}
                        className="!static !table-cell !border-0 !p-0 last:!static last:!right-auto last:!w-auto last:!bg-transparent group-data-[scrollable=true]:last:!shadow-none"
                        style={{ height: topSpacerHeight, position: 'static' }}
                      />
                    </TableRow>
                  )}

                  {virtualProjectRows.map((row, index) =>
                    renderProjectRow(row, virtualStartIndex + index)
                  )}

                  {bottomSpacerHeight > 0 && (
                    <TableRow aria-hidden="true" className="border-0 hover:bg-transparent">
                      <TableCell
                        colSpan={fullColSpan}
                        className="!static !table-cell !border-0 !p-0 last:!static last:!right-auto last:!w-auto last:!bg-transparent group-data-[scrollable=true]:last:!shadow-none"
                        style={{ height: bottomSpacerHeight, position: 'static' }}
                      />
                    </TableRow>
                  )}
                </>
              )
            ) : (
              <TableRow className="hover:bg-white">
                <TableCell
                  colSpan={fullColSpan}
                  className="text-center w-full max-w-none static h-full !table-cell"
                >
                  <div
                    style={{ gap: '16px' }}
                    className="h-full flex items-center justify-center flex-col"
                  >
                    {isFiltersActivate && (
                      <div>
                        <MagnifierIcon />
                      </div>
                    )}

                    <span className="text-2xl">No results</span>

                    {isFiltersActivate && handleResetAllFilters && (
                      <div>
                        <button
                          style={{ fontSize: '16px' }}
                          className="clear_filter_btn"
                          type="button"
                          onClick={() => handleResetAllFilters()}
                        >
                          Clear filters
                        </button>
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <PrintModal
        selectedItemsNumber={selectedProjectList.length}
        preparedItemsNumber={preparedSelectedProjectCount}
        accountId={0}
        isModalOpen={isPrintModalOpen}
        handlePrint={handlePrint ? () => Promise.resolve(handlePrint()) : undefined}
        onClose={() => openPrintModal?.(false)}
      />
    </div>
  );
};

export const SortTable: React.FC<Record<string, unknown>> = () => {
  return <div></div>;
};

export default ProjectOverviewTable;
